"use client"

import type React from "react"
import { useState } from "react"
import { useHistory } from "react-router-dom"
import {
  Button,
  TextField,
  Alert,
  AlertTitle,
  Box,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Typography,
  Grid,
} from "@mui/material"
import { CheckCircle } from "@mui/icons-material"
// import { takeDisciplinaryAction } from "../../Services/disciplinaryService"
import styles from "./DisciplinaryActionForm.module.css"
import { requestID } from "../../common/utils"
import { takeDisciplinaryAction } from "../../api/disciplinaryService"

interface DisciplinaryActionFormProps {
  caseId: string
  employeeId: string
  employeeName: string
  hasResponse: boolean
}

export default function DisciplinaryActionForm({
  caseId,
  employeeId,
  employeeName,
  hasResponse,
}: DisciplinaryActionFormProps) {
  const history = useHistory()
  const [actionType, setActionType] = useState("warning")
  const [comments, setComments] = useState("")
  const [penaltyType, setPenaltyType] = useState("salary_deduction")
  const [penaltyDuration, setPenaltyDuration] = useState("1")
  const [penaltyDurationType, setPenaltyDurationType] = useState("months")
  const [deductionAmount, setDeductionAmount] = useState("")
  const [accessRestriction, setAccessRestriction] = useState("")
  const [customPenaltyName, setCustomPenaltyName] = useState("")
  const [customPenaltyDetails, setCustomPenaltyDetails] = useState("")


  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const handleSubmit = async () => {
    if (isSubmitting) return // Prevent multiple submissions
    

    if (!comments.trim()) {
      setError("Please provide comments explaining your decision")
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append("disciplinary_id", caseId)
      formData.append("employee_id", employeeId)
      formData.append("actionType", actionType)
      formData.append("comments", comments)
      formData.append("requestID", requestID())

      if (actionType === "penalty") {
        formData.append("penaltyType", penaltyType)
        formData.append("penaltyDuration", penaltyDuration)
        formData.append("penaltyDurationType", penaltyDurationType)

        if (penaltyType === "salary_deduction") {
          formData.append("deductionAmount", deductionAmount)
        } else if (penaltyType === "access_restriction") {
          formData.append("accessRestriction", accessRestriction)
        } else if (penaltyType === "custom") {
          formData.append("customPenaltyName", customPenaltyName)
          formData.append("customPenaltyDetails", customPenaltyDetails)
        }
      }

      // Log form data for debugging
      for (const [key, value] of formData.entries()) {
        console.log(`${key}: ${value}`)
      }

      await takeDisciplinaryAction(formData)
      setSuccess(true)

      // Redirect after a short delay
      // setTimeout(() => {
      //   history.push(`/disciplinary/${caseId}`)
      // }, 6000)
    } catch (err: any) {
      setError(err.message || "Failed to submit action")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (success) {
    return (
      <Alert severity="success" icon={<CheckCircle fontSize="inherit" />}>
        <AlertTitle>Action Submitted</AlertTitle>
        Your disciplinary action has been submitted successfully. The page will refresh shortly.
      </Alert>
    )
  }

  if (!hasResponse) {
    return (
      <Alert severity="info">
        <AlertTitle>No Response Submitted</AlertTitle>
        The employee has not yet submitted a response to this query. You can still take action, but it's recommended to
        wait for their response.
      </Alert>
    )
  }

  return (
    // <form onSubmit={handleSubmit} className={styles.form}>
    <>
      {error && (
        <Alert severity="error" className={styles.alert}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      <FormControl component="fieldset" className={styles.formGroup}>
        <FormLabel component="legend">Action Type</FormLabel>
        <RadioGroup value={actionType} onChange={(e) => setActionType(e.target.value)}>
          <FormControlLabel value="warning" control={<Radio />} label="Issue Warning" />
          <FormControlLabel value="penalty" control={<Radio />} label="Apply Penalty" />
          <FormControlLabel value="close" control={<Radio />} label="Close Case (No Action)" />
        </RadioGroup>
      </FormControl>

      {actionType === "penalty" && (
        <Card variant="outlined" className={styles.penaltyCard}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Penalty Details
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="penalty-type-label">Penalty Type</InputLabel>
                  <Select
                    labelId="penalty-type-label"
                    id="penaltyType"
                    value={penaltyType}
                    onChange={(e) => setPenaltyType(e.target.value as string)}
                    label="Penalty Type"
                  >
                    <MenuItem value="salary_deduction">Salary Deduction</MenuItem>
                    <MenuItem value="access_restriction">Access Restriction</MenuItem>
                    <MenuItem value="suspension">Suspension</MenuItem>
                    <MenuItem value="probation">Probation</MenuItem>
                    <MenuItem value="demotion">Demotion</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <TextField
                  id="penaltyDuration"
                  label="Duration"
                  type="number"
                  inputProps={{ min: "1" }}
                  value={penaltyDuration}
                  onChange={(e) => setPenaltyDuration(e.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="duration-type-label">Duration Type</InputLabel>
                  <Select
                    labelId="duration-type-label"
                    id="penaltyDurationType"
                    value={penaltyDurationType}
                    onChange={(e) => setPenaltyDurationType(e.target.value as string)}
                    label="Duration Type"
                  >
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                    <MenuItem value="months">Month(s)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {penaltyType === "salary_deduction" && (
                <Grid item xs={12}>
                  <TextField
                    id="deductionAmount"
                    label="Deduction Amount (%)"
                    type="number"
                    inputProps={{ min: "1", max: "50" }}
                    placeholder="Enter percentage (1-50)"
                    value={deductionAmount}
                    onChange={(e) => setDeductionAmount(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    helperText="Specify the percentage of salary to be deducted each month for the duration."
                  />
                </Grid>
              )}

{penaltyType === "custom" && (
  <>
    <Grid item xs={12}>
      <TextField
        id="customPenaltyName"
        label="Custom Penalty Name"
        value={customPenaltyName}
        onChange={(e) => setCustomPenaltyName(e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        required
      />
    </Grid>
    <Grid item xs={12}>
      <TextField
        id="customPenaltyDetails"
        label="Penalty Description"
        placeholder="Describe the custom penalty"
        multiline
        rows={3}
        value={customPenaltyDetails}
        onChange={(e) => setCustomPenaltyDetails(e.target.value)}
        fullWidth
        variant="outlined"
        size="small"
        required
      />
    </Grid>
  </>
)}


              {penaltyType === "access_restriction" && (
                <Grid item xs={12}>
                  <TextField
                    id="accessRestriction"
                    label="Access Restriction Details"
                    placeholder="Specify which access will be restricted"
                    value={accessRestriction}
                    onChange={(e) => setAccessRestriction(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              )}
            </Grid>
            
          </CardContent>
        </Card>
      )}

      <div className={styles.formGroup}>
        <TextField
          id="comments"
          label="Comments"
          placeholder="Provide comments explaining your decision..."
          multiline
          rows={5}
          fullWidth
          required
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          variant="outlined"
          helperText="Explain your decision clearly. These comments will be visible to the employee and will be part of the permanent record."
        />
      </div>

      <Box className={styles.buttonContainer}>
        <Button
          type="button"
          variant="outlined"
          onClick={() => history.push(`/disciplinary/${caseId}`)}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? "Submitting..." : "Submit Action"}
        </Button>
      </Box>
      </>
    // {/* </form> */}
  )
}
